import React, { useEffect, useState, useMemo, useCallback, useRef, memo } from 'react';
import SideDrawer from '../../../SideDrawer/SideDrawer';
import DataTableFilterDrawerHeader from './components/DataTableFilterDrawerHeader';
import DataTableFilterDrawerFooter from './components/DataTableFilterDrawerFooter';
import { DataTableColumn, FilterRendererMap } from '../../DataTable.types';
import useFilterRenderers from '../../useFilterRenderers';
import styles from './DataTableFilterDrawer.module.scss';
import { useSelector } from 'react-redux';
import useDynamicFilterOptions from '../../useDynamicFilterOptions';
import LoadingSpinner from '../../../LoadingSpinner/LoadingSpinner';
import { filterCardsByLabel } from '../../utils';
import { filtersEmptyScreen } from '../../../../../assets/hostedassets';
import { FILTER_DRAWER_NO_MATCH_MESSAGE } from '../../DataTable.constants';

interface DataTableFilterDrawerProps {
    open: boolean;
    onClose: () => void;
    columns: DataTableColumn[];
    onApply: (filters: Record<string, any>) => void;
    width?: string;
    title?: string;
    customFilterRenderers?: FilterRendererMap;
    filterSourceKey: string;
}

const DataTableFilterDrawer: React.FC<DataTableFilterDrawerProps> = ({
    open,
    onClose,
    columns = [],
    onApply,
    width = '40%',
    title = 'Filters',
    customFilterRenderers,
    filterSourceKey,
}) => {
    const filterRenderers = useFilterRenderers(customFilterRenderers);
    const appliedFiltersFromStore = useSelector((state: any) => state.investor.customFilterDrawer.appliedFilters[filterSourceKey]);
    const { filterOptions, isLoading } = useDynamicFilterOptions(filterSourceKey, columns);

    // Memoize appliedFilters to prevent unnecessary re-renders
    const appliedFilters = useMemo(() => {
        return appliedFiltersFromStore ?? {};
    }, [appliedFiltersFromStore]);

    /* Only those columns are filterable which have a filterConfig with enabled: true and a filterType */
    const filterableColumns = useMemo(() => {
        return columns.filter((column) => column.filterConfig?.enabled && column.filterConfig?.filterType);
    }, [columns]);

    /* Function to initialize tempFilters with proper default values */
    const initializeTempFilters = useCallback(() => {
        const initialFilters: Record<string, any> = {};
        filterableColumns.forEach((column) => {
            // @todo: remove this once the getOrgBasedUsers api is updated
            if (column?.field === 'organizationOwner') {
                // Map owners.owner back to organizationOwner for display
                initialFilters[column?.field] = appliedFilters['owners.owner'] || appliedFilters[column?.field] || [];
            } else {
                initialFilters[column?.field] = appliedFilters[column?.field] || [];
            }
        });
        return initialFilters;
    }, [filterableColumns, appliedFilters]);

    const [tempFilters, setTempFilters] = useState<Record<string, any>>(() => initializeTempFilters());
    const [isDrawerReady, setIsDrawerReady] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const visibleColumns = useMemo(
        () => filterCardsByLabel(filterableColumns, searchQuery, (c) => c?.filterConfig?.label || c?.headerLabel || ''),
        [filterableColumns, searchQuery],
    );

    const isSearchActive = searchQuery.trim() !== '';
    const isEmptyResult = isSearchActive && visibleColumns.length === 0;

    // Keep the latest initializer in a ref so the init effect runs ONLY when the
    // drawer opens. Keying it on initializeTempFilters re-fires it on every parent
    // re-render (new columns array identity) while the drawer is open, silently
    // reverting unsaved edits — e.g. Clear All — back to the applied filters.
    const initializeTempFiltersRef = useRef(initializeTempFilters);
    useEffect(() => {
        initializeTempFiltersRef.current = initializeTempFilters;
    }, [initializeTempFilters]);

    useEffect(() => {
        if (open) {
            // Reset drawer ready state
            setIsDrawerReady(false);
            setSearchQuery('');

            // Defer content init by one short tick so the drawer's open animation
            // isn't janked by a heavy first-frame render — but not the full 1s the
            // backdrop blur takes (that left the sheet grey/blank for ~1s before
            // filters popped in). ~120ms is imperceptible and lets the slide start.
            const timer = setTimeout(() => {
                setTempFilters(initializeTempFiltersRef.current());
                setIsDrawerReady(true);
            }, 120);

            return () => clearTimeout(timer);
        } else {
            setIsDrawerReady(false);
        }
    }, [open]);

    const handleFilterChange = useCallback((field: string, value: any[]) => {
        setTempFilters((prev) => ({ ...prev, [field]: value || [] }));
    }, []);

    const handleApply = useCallback(() => {
        // Transform organizationOwner field to owners.owner for backend filtering
        const transformedFilters = { ...tempFilters };
        if (transformedFilters.organizationOwner) {
            transformedFilters['owners.owner'] = transformedFilters.organizationOwner;
            delete transformedFilters.organizationOwner;
        }
        onApply(transformedFilters);
        onClose();
    }, [tempFilters, onApply, onClose]);

    const handleClearAll = useCallback(() => {
        const clearedFilters: Record<string, any> = {};
        filterableColumns?.forEach((column) => {
            clearedFilters[column?.field] = [];
        });
        setTempFilters(clearedFilters);
    }, [filterableColumns]);

    /* Clear All is useful whenever anything is selected or applied — independent of hasChanges */
    const hasAnythingToClear = useMemo(() => {
        const anySelected = (obj: Record<string, any>) => Object.values(obj || {}).some((v) => (Array.isArray(v) ? v.length > 0 : !!v));
        return anySelected(tempFilters) || anySelected(appliedFilters);
    }, [tempFilters, appliedFilters]);

    /* Function to check if there are any changes to the filters */
    const hasChanges = useMemo(() => {
        for (const key in tempFilters) {
            const tempValue = tempFilters[key] || [];
            const appliedValue = appliedFilters[key] || [];
            if (tempValue.length !== appliedValue.length) return true;
            if (tempValue.length > 0 && JSON.stringify([...tempValue].sort()) !== JSON.stringify([...appliedValue].sort())) {
                return true;
            }
            // Check for differences in date range filters (gte/lte values)
            if (
                tempFilters[key]?.value?.gte !== appliedFilters[key]?.value?.gte ||
                tempFilters[key]?.value?.lte !== appliedFilters[key]?.value?.lte
            ) {
                return true;
            }
        }
        return false;
    }, [tempFilters, appliedFilters]);

    return (
        <>
            {open && (
                <SideDrawer width={width} onClose={onClose}>
                    <div className={styles.DrawerContainer}>
                        <div className={styles.DrawerHeader}>
                            <DataTableFilterDrawerHeader title={title} searchQuery={searchQuery} onSearchChange={setSearchQuery} />
                        </div>

                        <div className={styles.DrawerContent}>
                            {/* Only block on drawer-ready, NOT on isLoading — the dynamic
                                options (lenders/products) fetch async; render the filter
                                fields immediately and let those dropdowns populate when the
                                data arrives, instead of holding the whole drawer on a spinner
                                for the full network round-trip. */}
                            {!isDrawerReady ? (
                                <div className={styles.LoadingContainer}>
                                    <LoadingSpinner color={'#6021b3'} height="32px" />
                                </div>
                            ) : isEmptyResult ? (
                                <div className={styles.EmptyState}>
                                    <img src={filtersEmptyScreen} alt="" className={styles.EmptyStateImage} />
                                    <p className={styles.EmptyStateText}>
                                        {FILTER_DRAWER_NO_MATCH_MESSAGE}{' '}
                                        <button type="button" className={styles.ClearSearchLink} onClick={() => setSearchQuery('')}>
                                            Clear filters
                                        </button>
                                    </p>
                                </div>
                            ) : (
                                <div className={styles.FiltersContainer}>
                                    {visibleColumns.map((column) => {
                                        const FilterRenderer = filterRenderers[column?.filterConfig?.filterType ?? ''];
                                        if (!FilterRenderer) {
                                            console.warn(`No filter renderer found for filter type: ${column?.filterConfig?.filterType}`);
                                            return null;
                                        }

                                        // Get options from filterOptions or column.dropdownOptions
                                        const options =
                                            filterOptions?.[column?.field] ||
                                            (column?.dropdownOptions
                                                ? Object.values(column?.dropdownOptions)
                                                      .filter((option) => !column?.filterConfig?.excludedValues?.includes(option.value))
                                                      .map((option) => ({
                                                          label: option?.label || '',
                                                          value: option?.value || '',
                                                      }))
                                                : []);
                                        return (
                                            <FilterRenderer
                                                key={column?.field}
                                                column={column}
                                                value={tempFilters[column?.field] || []}
                                                onChange={(value) => handleFilterChange(column?.field, value)}
                                                options={options}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <DataTableFilterDrawerFooter
                            onSave={handleApply}
                            onClearAll={handleClearAll}
                            saveDisabled={!hasChanges || !isDrawerReady || isLoading}
                            clearDisabled={!hasAnythingToClear || !isDrawerReady || isLoading}
                            saveText="Apply"
                            cancelText="Clear All"
                        />
                    </div>
                </SideDrawer>
            )}
        </>
    );
};

export default memo(DataTableFilterDrawer);
